import React, { useState, useEffect } from 'react';
import { adminService } from '../../services/adminService';
import { User } from '../../types/user';
import { CrisisCommunicationUser } from '../../types/crisisCommunicationUser';
import RevokeTokenModal from '../Insurer/RevokeTokenModal';

interface UserDetailsProps {
  user: User;
  onClose: () => void;
  onUserUpdated: () => void;
}

const UserDetails: React.FC<UserDetailsProps> = ({ user, onClose, onUserUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [crisisCommunications, setCrisisCommunications] = useState<CrisisCommunicationUser[]>([]);
  const [loadingCrisisCommunications, setLoadingCrisisCommunications] = useState(false);
  const [insurerOrgName, setInsurerOrgName] = useState<string | null>(null);
  const [showRevokeModal, setShowRevokeModal] = useState(false);

  useEffect(() => {
    loadCrisisCommunications();
  }, [user.id]);

  useEffect(() => {
    const loadOrgName = async () => {
      if (!user.insurer_org_id) {
        setInsurerOrgName(null);
        return;
      }
      try {
        const detail = await adminService.getInsurerOrgDetail(user.insurer_org_id);
        setInsurerOrgName(detail.org.name);
      } catch {
        setInsurerOrgName(`Org #${user.insurer_org_id}`);
      }
    };
    loadOrgName();
  }, [user.insurer_org_id]);

  const loadCrisisCommunications = async () => {
    try {
      setLoadingCrisisCommunications(true);
      const data = await adminService.getUserCrisisCommunications(user.id);
      setCrisisCommunications(data);
    } catch (err: any) {
      console.error('Failed to load crisis communications:', err);
    } finally {
      setLoadingCrisisCommunications(false);
    }
  };

  const handleUserAction = async (action: string) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      switch (action) {
        case 'activate':
          await adminService.activateUser(user.id);
          setSuccess('User activated successfully');
          break;
        case 'deactivate':
          await adminService.deactivateUser(user.id);
          setSuccess('User deactivated successfully');
          break;
        case 'reset_password':
          if (!newPassword) {
            setError('Please enter a new password');
            setLoading(false);
            return;
          }
          await adminService.resetUserPassword(user.id, newPassword);
          setSuccess('Password reset successfully');
          setShowPasswordReset(false);
          setNewPassword('');
          break;
      }
      onUserUpdated();
    } catch (err: any) {
      setError(err.response?.data?.detail || `Failed to ${action.replace('_', ' ')} user`);
    } finally {
      setLoading(false);
    }
  };

  const isUserLocked = adminService.isUserLocked(user);

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-medium text-gray-900">User Details</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Status Messages */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-800">{success}</p>
                </div>
              </div>
            </div>
          )}

          {/* User Information */}
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Email</label>
                  <p className="mt-1 text-sm text-gray-900">{user.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Full Name</label>
                  <p className="mt-1 text-sm text-gray-900">{user.full_name || 'Not provided'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Role</label>
                  <span className={`mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${adminService.getRoleBadgeColor(user.role)}`}>
                    {adminService.getRoleDisplayName(user.role)}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Status</label>
                  <div className="mt-1 flex space-x-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {isUserLocked && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Locked
                      </span>
                    )}
                    {user.is_superuser && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        Super User
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {(user.insurer_org_id || user.org_role) && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Partner organization</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Organization</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {insurerOrgName || (user.insurer_org_id ? `Org #${user.insurer_org_id}` : 'Not linked')}
                    </p>
                  </div>
                  {user.org_role && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Org role</label>
                      <p className="mt-1 text-sm text-gray-900">{user.org_role.replace('_', ' ')}</p>
                    </div>
                  )}
                </div>
                {user.role === 'USER' && user.insurer_org_id && (
                  <div className="mt-4 flex gap-2">
                    <button
                      type="button"
                      disabled={loading}
                      onClick={async () => {
                        setLoading(true);
                        setError(null);
                        try {
                          await adminService.grantClientCredit(user.insurer_org_id!, user.id);
                          setSuccess('Partner credit granted');
                          onUserUpdated();
                        } catch (err: any) {
                          setError(err?.response?.data?.detail || 'Grant failed');
                        } finally {
                          setLoading(false);
                        }
                      }}
                      className="px-3 py-1.5 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      Grant tabletop credit
                    </button>
                    <button
                      type="button"
                      disabled={loading}
                      onClick={() => setShowRevokeModal(true)}
                      className="px-3 py-1.5 text-sm font-medium rounded-md border border-amber-500 text-amber-800 hover:bg-amber-50 disabled:opacity-50"
                    >
                      Revoke unused credit
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Activity Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Activity Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Created</label>
                  <p className="mt-1 text-sm text-gray-900">{adminService.formatDate(user.created_at)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Last Updated</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {user.updated_at ? adminService.formatDate(user.updated_at) : 'Never'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Last Login</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {user.last_login ? adminService.formatDate(user.last_login) : 'Never'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Login Attempts</label>
                  <p className="mt-1 text-sm text-gray-900">{user.login_attempts}</p>
                </div>
              </div>
            </div>

            {/* Notes */}
            {user.notes && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-medium text-gray-900 mb-2">Notes</h4>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{user.notes}</p>
              </div>
            )}

            {/* Crisis Communications */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Crisis Communications</h4>
              {loadingCrisisCommunications ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-sm text-gray-600">Loading...</span>
                </div>
              ) : crisisCommunications.length > 0 ? (
                <div className="space-y-3">
                  {crisisCommunications.map((cc) => (
                    <div key={cc.id} className="bg-white rounded-lg p-3 border border-gray-200">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h5 className="font-medium text-gray-900">
                            {cc.first_name} {cc.middle_name ? `${cc.middle_name} ` : ''}{cc.last_name}
                          </h5>
                          <p className="text-sm text-gray-600">{cc.email}</p>
                        </div>
                        <span className="text-xs text-gray-500">
                          {adminService.formatDate(cc.created_at)}
                        </span>
                      </div>
                      {cc.phone && (
                        <p className="text-sm text-gray-600 mb-1">
                          <span className="font-medium">Phone:</span> {cc.phone}
                        </p>
                      )}
                      {cc.notes && (
                        <p className="text-sm text-gray-700 mt-2">
                          <span className="font-medium">Notes:</span> {cc.notes}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">
                  No crisis communication entries found for this user.
                </p>
              )}
            </div>

            {/* Password Reset */}
            {showPasswordReset && (
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Reset Password</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Password
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter new password"
                    />
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => handleUserAction('reset_password')}
                      disabled={loading || !newPassword}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Resetting...' : 'Reset Password'}
                    </button>
                    <button
                      onClick={() => {
                        setShowPasswordReset(false);
                        setNewPassword('');
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-6 flex justify-between">
            <div className="flex space-x-3">
              {user.is_active ? (
                <button
                  onClick={() => handleUserAction('deactivate')}
                  disabled={loading || user.role === 'SUPER_ADMIN' || user.is_superuser}
                  className="px-4 py-2 text-sm font-medium text-yellow-700 bg-yellow-100 rounded-md hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Deactivating...' : 'Deactivate User'}
                </button>
              ) : (
                <button
                  onClick={() => handleUserAction('activate')}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-green-700 bg-green-100 rounded-md hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Activating...' : 'Activate User'}
                </button>
              )}

              <button
                onClick={() => setShowPasswordReset(!showPasswordReset)}
                className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {showPasswordReset ? 'Cancel Reset' : 'Reset Password'}
              </button>
            </div>

            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      <RevokeTokenModal
        isOpen={showRevokeModal}
        client={{
          id: user.id,
          email: user.email,
          full_name: user.full_name,
          credits_remaining: 1,
          is_registered: user.is_active,
        }}
        onClose={() => setShowRevokeModal(false)}
        onConfirm={async () => {
          if (!user.insurer_org_id) return;
          await adminService.revokeClientCredit(user.insurer_org_id, user.id);
          onUserUpdated();
          return 'Partner credit revoked successfully';
        }}
        onRevoked={(msg) => {
          setSuccess(msg);
          setShowRevokeModal(false);
        }}
        onError={(err) => {
          setError(err);
        }}
      />
    </div>
  );
};

export default UserDetails;
